// The project P&L tab's inline cell editor.
//
// SEQ-180 phase 2, P24: lifted out of ProjectStatusTab.tsx unchanged in
// look and behaviour, then given a unit, so the same popover serves the
// dollar category rows and the new manually entered operational rows. One
// editor, one set of keyboard rules, rather than a second copy that drifts.
//
// The currency path is deliberately byte-for-byte what it was: the big "$"
// glyph, step 0.01, and `parseFloat(value) || 0` so a blank saves as zero.
// A non-currency unit gets a suffix instead of the "$" and, when
// `allowClear` is set, saves null for a blank input: a manual metric needs
// "no value" to be a real state, distinct from a typed 0, or D27's mean
// would be computed over months nobody ever measured.

import { useState } from "react";
import { Loader2 } from "lucide-react";
import type { PnlUnit } from "../../lib/pnl/pnlAnalytics";
import { Modal } from "../ui/ToastModal";
import { MoneyInput } from "../Money";
import { MONTH_NAMES } from "../../lib/calculations";

/** The short text that sits next to the input for a non-currency unit. Currency uses the leading "$" glyph instead. */
const UNIT_SUFFIX: Record<PnlUnit, string> = {
  currency: "",
  currency_per_hour: "/hr",
  percent: "%",
  hours: "hrs",
  count: "",
  ratio: "",
};

function shortMonth(month: number): string {
  return (MONTH_NAMES[month - 1] || "").slice(0, 3);
}

interface PnlEditCellModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Receives null only when `allowClear` is set and the field was left blank. */
  onSave: (value: number | null) => void;
  /** Row label, shown under the month heading. */
  categoryName: string;
  month: number;
  year: number;
  currentValue: number | null;
  saving: boolean;
  /** Defaults to currency, which reproduces the original dollar editor exactly. */
  unit?: PnlUnit;
  /** Input step. Defaults to 0.01. */
  step?: string;
  /** When true, a blank field saves null (clears the value) instead of 0. */
  allowClear?: boolean;
  /** One line under the input, e.g. what an averaged metric does with the value. */
  hint?: string;
}

export function PnlEditCellModal({
  isOpen,
  onClose,
  onSave,
  categoryName,
  month,
  year,
  currentValue,
  saving,
  unit = "currency",
  step = "0.01",
  allowClear = false,
  hint,
}: PnlEditCellModalProps) {
  const [value, setValue] = useState(() => (currentValue === null ? "" : currentValue.toString()));

  if (!isOpen) return null;

  const isCurrency = unit === "currency";
  const suffix = UNIT_SUFFIX[unit];

  function commit() {
    const trimmed = value.trim();
    if (allowClear && trimmed === "") {
      onSave(null);
      return;
    }
    onSave(parseFloat(trimmed) || 0);
  }

  // Design audit, LOW: this was a hand-rolled overlay with no dialog role, no
  // aria-modal, no focus trap and a set of literal hexes. It now renders
  // through the shared Modal, which carries all three, so a fix there reaches
  // this editor too. The footer stays custom (hideFooter) only because the
  // Save button shows a spinner the shared footer has no slot for.
  return (
    <Modal
      isOpen
      onClose={onClose}
      size="sm"
      maxWidth={340}
      hideFooter
      ariaLabel={`Edit ${categoryName} for ${shortMonth(month)} ${year}`}
      title={
        <div>
          <div style={{ fontWeight: 600, fontSize: 16, color: "var(--text-primary)" }}>
            {shortMonth(month)} {year}
          </div>
          <div style={{ color: "var(--text-secondary)", fontSize: 13, marginTop: 2 }}>{categoryName}</div>
        </div>
      }
    >
      <div>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            marginBottom: hint ? 8 : 20,
          }}
        >
          {isCurrency && (
            <span
              style={{ fontSize: 28, fontWeight: 700, color: "var(--text-secondary)" }}
            >
              $
            </span>
          )}
          <MoneyInput
            type="number"
            className="form-input"
            aria-label={`${categoryName}, ${shortMonth(month)} ${year}`}
            style={{
              flex: 1,
              fontSize: 28,
              fontWeight: 700,
              textAlign: "center",
              padding: 8,
              border: "2px solid var(--border-color)",
              borderRadius: 12,
              color: "var(--text-primary)",
            }}
            value={value}
            onChange={(e) => setValue(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") commit();
              if (e.key === "Escape") onClose();
            }}
            autoFocus
            step={step}
          />
          {!isCurrency && suffix && (
            <span style={{ fontSize: 20, fontWeight: 600, color: "var(--text-secondary)" }}>{suffix}</span>
          )}
        </div>

        {hint && (
          <div style={{ fontSize: 12, color: "var(--text-secondary)", marginBottom: 20, lineHeight: 1.5 }}>
            {hint}
          </div>
        )}

        <div
          style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}
        >
          <button
            className="btn btn-ghost"
            onClick={onClose}
            disabled={saving}
            style={{ fontSize: 13 }}
          >
            Cancel
          </button>
          <button
            className="btn btn-primary"
            onClick={commit}
            disabled={saving}
            style={{ fontSize: 13, display: "flex", alignItems: "center", gap: 6 }}
          >
            {saving ? (
              <Loader2 size={14} className="spinner" />
            ) : null}
            {saving ? "Saving…" : "Save"}
          </button>
        </div>
      </div>
    </Modal>
  );
}
